import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HeaderComponent } from '../../atoms/header/header.component';
import { StatCardComponent } from '../../atoms/stat-card/stat-card.component';
import { BookmarkService } from '../../../services/bookmark/bookmark.service';
import { BookmarkPublicationService } from '../../../services/bookmark/bookmark-publication.service';
import { BookmarkValidationService } from '../../../services/bookmark-validation.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { BookmarkInventory } from '../../../models/bookmark-inventory';
import {
  applyInventoryFilter,
  countToPublish,
  dataStateTag,
  describeToPublish,
  indicatorFullName,
  widgetLabel,
  statusTag,
} from '../../../utility/bookmark-inventory.utility';

/** Font Awesome icon per widget type */
const WIDGET_ICONS: Record<string, string> = {
  map: 'fas fa-map',
  bar: 'fas fa-chart-column',
  line: 'fas fa-chart-line',
  pie: 'fas fa-chart-pie',
  scatter: 'fas fa-braille',
  bubble: 'fas fa-circle-nodes',
  radar: 'fas fa-bullseye',
  polarArea: 'fas fa-chart-pie',
  table: 'fas fa-table',
  text: 'fas fa-align-left',
  info: 'fas fa-circle-info',
  teaser: 'fas fa-image',
};

const AUDIENCE_LABELS: Record<string, string> = {
  public: 'öffentlich',
  guest: 'Gast-Link',
  private: 'privat',
  internal: 'intern',
};

/**
 * Overlay "Nutzung & Freigabe": everything a view uses - widgets,
 * indicators, spaces & years, indicator data, other references - with their
 * status, and what is still missing for publishing it ("Fehlendes
 * freigeben"). Data comes from GET /bookmark/:id/inventory.
 */
@Component({
  selector: 'app-bookmark-inventory-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputSwitchModule,
    HeaderComponent,
    StatCardComponent,
  ],
  templateUrl: './bookmark-inventory-panel.component.html',
  styleUrl: './bookmark-inventory-panel.component.scss',
})
export class BookmarkInventoryPanelComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly bookmarkService = inject(BookmarkService);
  private readonly publicationService = inject(BookmarkPublicationService);
  private readonly validationService = inject(BookmarkValidationService);
  private readonly confirmationDialogService = inject(
    ConfirmationDialogService
  );
  private readonly messageService = inject(MessageService);
  private readonly bookmarkStore = inject(BookmarkStore);
  private readonly globalsStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);

  readonly inventory = signal<BookmarkInventory | null>(null);
  readonly loading = signal(false);
  readonly publishing = signal(false);
  readonly error = signal<string | null>(null);
  readonly selectedWidgetId = signal<string | null>(null);
  readonly onlyProblems = signal(false);

  readonly statusTag = statusTag;
  readonly dataStateTag = dataStateTag;
  readonly indicatorFullName = indicatorFullName;
  readonly widgetLabel = widgetLabel;

  readonly view = computed(() => {
    const inventory = this.inventory();
    return inventory
      ? applyInventoryFilter(inventory, {
          widgetId: this.selectedWidgetId(),
          onlyProblems: this.onlyProblems(),
        })
      : null;
  });

  /**
   * The widget list is not narrowed by the widget selection: all widgets stay
   * visible (the others dimmed) so the selection is obvious and switching
   * works with a single click. The selected one stays even if "nur Probleme"
   * would hide it.
   */
  readonly widgetList = computed(() => {
    const inventory = this.inventory();
    if (!inventory) return [];
    const selectedId = this.selectedWidgetId();
    const visible = new Set(
      applyInventoryFilter(inventory, {
        widgetId: null,
        onlyProblems: this.onlyProblems(),
      }).widgets.map(widget => widget.id)
    );
    return inventory.widgets.filter(
      widget => visible.has(widget.id) || widget.id === selectedId
    );
  });

  readonly toPublishCount = computed(() => {
    const inventory = this.inventory();
    return inventory ? countToPublish(inventory.readiness.toPublish) : 0;
  });

  readonly selectedWidget = computed(() =>
    this.inventory()?.widgets.find(w => w.id === this.selectedWidgetId())
  );

  /**
   * Publishing needs write rights: not read-only (level 10) and not
   * workspace-based (level 0 - publishes item by item); the server checks
   * the same (bookmark.controller.js#publishRequirements).
   */
  readonly canPublish = computed(() => {
    const user = this.activeUserStore.user();
    if (!user || this.activeUserStore.isReadOnly()) return false;
    return user.isAdmin || user.isSuperAdmin || user.level > 0;
  });

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      this.selectedWidgetId.set(null);
      if (id) this.load(id);
    });
  }

  async load(bookmarkId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      this.inventory.set(await this.bookmarkService.getInventory(bookmarkId));
    } catch (error) {
      this.inventory.set(null);
      this.error.set(
        error instanceof HttpErrorResponse && error.status === 404
          ? 'Die Ansicht wurde nicht gefunden.'
          : 'Die Übersicht konnte nicht geladen werden.'
      );
    } finally {
      this.loading.set(false);
    }
  }

  toggleWidget(widgetId: string): void {
    this.selectedWidgetId.update(current =>
      current === widgetId ? null : widgetId
    );
  }

  /** Widget label for an issue: "#3 Ohne Überschrift" instead of an indicator name */
  issueWidgetLabel(widgetId: string): string {
    const widget = this.inventory()?.widgets.find(w => w.id === widgetId);
    return widget ? `#${widget.position} ${widgetLabel(widget)}` : 'Ansicht';
  }

  widgetIcon(type: string): string {
    return WIDGET_ICONS[type] || 'fas fa-square';
  }

  audienceLabel(audience: string): string {
    return AUDIENCE_LABELS[audience] || audience;
  }

  /** "Fehlendes freigeben": publishes what the view still needs. */
  async publishMissing(): Promise<void> {
    const inventory = this.inventory();
    if (!inventory || this.toPublishCount() === 0) return;

    const confirmed =
      await this.confirmationDialogService.confirmPublishRequirements(
        inventory.bookmark.title,
        describeToPublish(inventory.readiness.toPublish)
      );
    if (!confirmed) return;

    this.publishing.set(true);
    try {
      const result = await this.publicationService.publishRequirements(
        inventory.bookmark._id
      );
      this.inventory.set(result.inventory);
      this.messageService.add({
        severity: 'success',
        summary: 'Freigegeben',
        detail:
          describeToPublish(result.published).join(', ') + ' veröffentlicht.',
        life: 4000,
      });
      this.refreshValidation();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail:
          error instanceof HttpErrorResponse && error.status === 403
            ? 'Für die Freigabe fehlen Ihnen die Rechte.'
            : 'Die Freigabe ist fehlgeschlagen.',
        life: 5000,
      });
    } finally {
      this.publishing.set(false);
    }
  }

  /** Publishes the view itself once everything it needs is public. */
  async publishView(): Promise<void> {
    const inventory = this.inventory();
    const bookmark =
      inventory && this.bookmarkStore.getById(inventory.bookmark._id);
    if (!bookmark) return;
    if (!(await this.validationService.confirmPublication(bookmark))) return;
    this.bookmarkStore.update({ ...bookmark, published: true });
    this.inventory.update(current =>
      current
        ? {
            ...current,
            bookmark: {
              ...current.bookmark,
              published: true,
              audience: current.bookmark.private
                ? current.bookmark.audience
                : 'public',
            },
          }
        : current
    );
  }

  private refreshValidation(): void {
    this.validationService
      .validateBookmarksWithServer()
      .then(summary => this.globalsStore.setBookmarkValidation(summary))
      .catch(() => undefined);
  }
}
